import React, { useEffect, useRef, useState } from 'react';
import { CmAlertDialog } from '@codametrix/ui-components';
import { Button } from '../button/button';
import Input from '../input/input';
import { ButtonVariant, InputVariant } from '../../core/enums';
import { CoderListSection } from '../coder-list-section/coder-list-section';
import { CoderListItemProps } from '../coder-list-item/coder-list-item';
import './manage-coders-modal.scss';

export interface ManageCodersModalProps {
  isOpen: boolean;
  queueName: string;
  assignedCoders: CoderListItemProps[];
  availableCoders: CoderListItemProps[];
  onSave: () => void;
  onCancel: () => void;
  saveDisabled?: boolean;
}

export const ManageCodersModal: React.FC<ManageCodersModalProps> = ({
  isOpen,
  queueName,
  assignedCoders,
  availableCoders,
  onSave,
  onCancel,
  saveDisabled = false
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [query, setQuery] = useState('');
  const [announced, setAnnounced] = useState('');

  // The container renders this modal unconditionally, so it never unmounts and a
  // stale query would survive a reopen.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setAnnounced('');
    }
  }, [isOpen]);

  const needle = query.trim().toLowerCase();
  const matches = (coder: CoderListItemProps) =>
    coder.coderName.toLowerCase().includes(needle);

  const filteredAssigned = needle ? assignedCoders.filter(matches) : assignedCoders;
  const filteredAvailable = needle ? availableCoders.filter(matches) : availableCoders;

  useEffect(() => {
    const id = setTimeout(() => {
      setAnnounced(
        needle
          ? `${filteredAssigned.length} assigned, ${filteredAvailable.length} available match`
          : ''
      );
    }, 400);
    return () => clearTimeout(id);
  }, [needle, filteredAssigned.length, filteredAvailable.length]);

  const footer = (
    <>
      <Button
        ref={cancelRef}
        label="Cancel"
        variant={ButtonVariant.SECONDARY}
        onClick={onCancel}
        dataTestId="manage-coders-cancel"
      />
      <Button
        label="Save Assignments"
        disabled={saveDisabled}
        onClick={onSave}
        dataTestId="manage-coders-save"
      />
    </>
  );

  return (
    <CmAlertDialog
      isOpen={isOpen}
      onClose={onCancel}
      title={`Manage Coders - ${queueName}`}
      leastDestructiveRef={cancelRef}
      footer={footer}
      variant="wide"
      maxW="720px"
      contentClassName="cmx-manage-coders-dialog"
      contentTestId="manage-coders-modal"
    >
      <div className="cmx-manage-coders-modal">
        <p className="cmx-manage-coders-modal__subtitle">
          Add or remove coders from this queue. Coders must have the appropriate
          skill set to be assigned.
        </p>
        <div className="cmx-manage-coders-modal__search">
          <Input
            variant={InputVariant.SEARCH}
            placeholder="Search"
            aria-label="Search coders"
            label=""
            value={query}
            onChange={e => setQuery(e.target.value)}
            dataTestId="manage-coders-search"
          />
          <span
            className="cmx-manage-coders-modal__status"
            aria-live="polite"
            role="status"
          >
            {announced}
          </span>
        </div>
        <div className="cmx-manage-coders-modal__sections">
          <CoderListSection
            title="Currently Assigned"
            coders={filteredAssigned}
            total={assignedCoders.length}
            isFiltered={Boolean(needle)}
          />
          <CoderListSection
            title="Available Coders"
            coders={filteredAvailable}
            total={availableCoders.length}
            isFiltered={Boolean(needle)}
          />
        </div>
      </div>
    </CmAlertDialog>
  );
};

ManageCodersModal.displayName = 'ManageCodersModal';
